/**
 * 本家サーバーの過去ログ（アーカイブ）を読み込む。
 *
 * `http://balloon.matrix.jp/GoalView/archive/<日付><am|pm>.txt` に、
 * ライブと同じ中継ストリーム行フォーマット（`<端末ID 8バイト> <経過ms 8桁>
 * <センテンス>`）で1日ぶんのログが置かれている。ブラウザはHTTPS→HTTP
 * （混在コンテンツ）・CORS無しの本家サーバーへ直接は出られないため、
 * ライブ接続と同じ理由でGV Bridgeの `/api/archive` に代理取得してもらう。
 *
 * 取り込みはIGC読み込みと同じ「複数機ぶんを一括で航跡一覧に追加する」
 * 流儀に合わせる（利用者の希望どおり）。
 */

import {
  bytesToLatin1,
  decodeTerminalIdLabel,
  StreamDecoder,
  type CircleArea,
  type FixSample,
  type MarkPoint,
} from '@gv4d/core';
import type { LoadedFlight } from './venue.ts';

/** 過去ログ1件から取り出せる全データ。航跡だけでなく、他機が宣言した
 * ゴール（マーク点）・競技エリア（円）も含む——利用者からの指摘、
 * 2026-09-15:「過去ログ取得時にゴールやスケッチのデータが入っているか
 * 確認してほしい」。実際に取得したログを解析したところ、GV4D独自の
 * 「ゴール一覧」「スケッチ」機能（どちらもlocalStorage限定で通信に
 * 一切乗らない）とは別に、原典プロトコルの`$_TVMARK`/`$_TVCIRCLE`
 * （他機が宣言したゴール・競技エリア）が実データとして残っていることを
 * 確認した。以前はこれをすべて捨てて航跡だけ返していた。 */
export interface ArchiveData {
  flights: LoadedFlight[];
  marks: MarkPoint[];
  circles: CircleArea[];
}

/** `YYYYMMDD` + セッション名（本家サーバーの命名そのまま。例: "am"/"pm"）からファイル名を組み立てる */
export function archiveFileName(dateYmd: string, session: string): string {
  return `${dateYmd}${session}.txt`;
}

/**
 * ファイル名（例: "20260823am.txt"）の日付部分を、UTC正午のepoch msにする。
 * センテンス自身の年月日が明らかにおかしい（RTC未同期の端末など）かどうかを
 * 判定する基準時刻に使う（`isPlausibleAbsoluteMs`）。取れなければnull
 * （その場合は呼び出し側で`Date.now()`に任せればよい。5年以内という
 * 判定の余裕があるので、多くの場合は実用上問題にならない）。
 */
export function archiveReferenceMs(fileName: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})/.exec(fileName);
  if (!m) return null;
  const [, y, mo, d] = m;
  return Date.UTC(Number(y), Number(mo) - 1, Number(d), 12, 0, 0);
}

/**
 * ブリッジ経由で過去ログを取得し、端末ごとのフライト・マーク点・
 * 競技エリアへ組み立てる。1本のファイルに複数機（＋タスク管理用の
 * 疑似端末等）が混在するので、実際に測位を持つ端末だけをフライトとして
 * 返す。
 *
 * ★ `zone`（UTMゾーン）を渡さないと`$_TVMARK`/`$_TVCIRCLE`
 *   （他機が宣言したゴール・競技エリア）が座標に復元できず、
 *   `StreamDecoder`が黙って`passthrough`へ回してしまう（マーク点・
 *   競技エリアが0件になる）。ライブ受信（`main.ts`の`zone: venueMap?.utmZone`）
 *   と同じ値を呼び出し側から渡すこと。
 */
export async function loadArchiveFlights(bridgeBaseUrl: string, fileName: string, zone?: number): Promise<ArchiveData> {
  const base = bridgeBaseUrl.replace(/\/+$/, '');
  const res = await fetch(`${base}/api/archive?file=${encodeURIComponent(fileName)}`);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`過去ログを取得できません (HTTP ${res.status})${body ? `: ${body}` : ''}`);
  }
  const bytes = new Uint8Array(await res.arrayBuffer());
  const text = bytesToLatin1(bytes);

  const decoder = new StreamDecoder();
  const receivedAtMs = archiveReferenceMs(fileName) ?? Date.now();
  const result = decoder.decode(text, { receivedAtMs, source: 'replay', zone });

  const byTerminal = new Map<string, FixSample[]>();
  for (const fix of result.fixes) {
    const list = byTerminal.get(fix.terminalId);
    if (list) list.push(fix);
    else byTerminal.set(fix.terminalId, [fix]);
  }

  const flights: LoadedFlight[] = [];
  for (const [terminalId, fixes] of byTerminal) {
    flights.push({
      label: decodeTerminalIdLabel(terminalId) || terminalId,
      terminalId,
      fixes,
      recorder: `過去ログ（${fileName}）`,
    });
  }
  const marks = [...result.marks.values()].flat();
  const circles = [...result.circles.values()].flat();
  if (flights.length === 0 && marks.length === 0 && circles.length === 0) {
    throw new Error('この過去ログには有効なデータが含まれていません');
  }
  // 一覧の見た目を安定させるため、端末IDの辞書順にしておく
  flights.sort((a, b) => a.terminalId.localeCompare(b.terminalId));
  return { flights, marks, circles };
}
